import React, { useEffect } from "react";
import CaseStudyLayout from "../CaseStudyLayout";

const MyProtocol = () => {
  useEffect(() => {
    document.title = "My Protocol Case Study - Andy Persaud Portfolio";
    window.scrollTo(0, 0);
  }, []);

  const usabilityFixes = [
    {
      issue: "Meal context bug: food detail always showed &quot;Snacks&quot;",
      impact: "High",
      fix: "Pass correct meal context via nav params + fallback from log entry"
    },
    {
      issue: "Barcode &quot;Add&quot; button unresponsive",
      impact: "High",
      fix: "Pressable component + z-index fix"
    },
    {
      issue: "Home graphs not updating after logging",
      impact: "High",
      fix: "Global refresh trigger / shared data hydration hook"
    },
    {
      issue: "Progress bars missing on Today page",
      impact: "Medium",
      fix: "Restored rendering logic for progress indicators"
    },
    {
      issue: "Calorie ring inaccurate (full with calories remaining)",
      impact: "High",
      fix: "Rebuilt consumed/target ratio, clamp 0-1"
    },
    {
      issue: "Cold start zero/empty data states",
      impact: "Medium",
      fix: "Skeleton states + shared hydration"
    },
    {
      issue: "&quot;Food not found&quot; when tapping logged items",
      impact: "Medium",
      fix: "Synthetic food object from log entry data"
    },
    {
      issue: "Exercise search capped at 100 results",
      impact: "Medium",
      fix: "Increased to full library across contexts"
    },
    {
      issue: "Custom exercise creation dead end",
      impact: "Medium",
      fix: "Auto-add to active workout/routine + return to context"
    },
    {
      issue: "Input sanitization blocked spaces in exercise names",
      impact: "Low",
      fix: "Removed premature sanitization"
    }
  ];

  const successCriteria = [
    { area: "Today Page", criteria: "User understands context + &quot;what next&quot; in &lt;3-5 seconds" },
    { area: "Food Logging", criteria: "≤3 taps from intent to logged food; meal context preserved" },
    { area: "Workout Logging", criteria: "Log exercise + 3 sets in &lt;30 seconds" },
    { area: "Insights", criteria: "Deterministic, rule-based logic (no unpredictable AI text)" },
    { area: "Tone", criteria: "Observational language only; no punitive streaks" },
    { area: "Resilience", criteria: "No broken/lying states; graceful degradation" }
  ];

  const designPrinciples = [
    { principle: "Energy over activity", description: "Focus on meaningful output, not busywork metrics" },
    { principle: "Insight over data", description: "Interpret numbers, don&apos;t just display them" },
    { principle: "Calm over urgent", description: "Reduce anxiety; no guilt-driven language" },
    { principle: "Progressive over locked", description: "Tease value, don&apos;t block entirely" },
    { principle: "Graceful over broken", description: "Handle edge cases without dead ends" },
    { principle: "Speed for high-frequency tasks", description: "Minimize taps for daily logging" }
  ];

  return (
    <CaseStudyLayout>
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-900 to-slate-800 section-padding">
        <div className="container-narrow">
          <div className="text-center mb-12">
            <h1 className="heading-hero animate-slide-up text-white mb-4">My Protocol</h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed animate-slide-up mb-8">
              A premium fitness and nutrition tracking app for serious trainees who want clarity, accuracy, and insight—not gamification.
            </p>
          </div>

          {/* Hero App Screenshot */}
          <div className="flex justify-center mb-12">
            <img
              src="https://customer-assets.emergentagent.com/job_1def07b2-6293-4c22-9598-3b0db649a536/artifacts/9vyclw9v_Today.jpg"
              alt="My Protocol Today screen - Your Training, Nutrition, and Energy Connected Clearly"
              className="w-full max-w-2xl rounded-2xl shadow-2xl animate-fade-in"
            />
          </div>

          {/* Project Overview */}
          <div className="grid md:grid-cols-4 gap-8 bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-8 animate-scale-in">
            <div className="text-center">
              <h3 className="font-semibold text-slate-200 mb-2">Role</h3>
              <p className="text-slate-400">UX / Product Design (Mobile)</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-slate-200 mb-2">Platform</h3>
              <p className="text-slate-400">iOS &amp; Android (React Native)</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-slate-200 mb-2">Timeline</h3>
              <p className="text-slate-400">Multi-Sprint Feature Cycle</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-slate-200 mb-2">Engagement</h3>
              <p className="text-slate-400">Foundation + Iterative Sprints</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="section-padding bg-white">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-4">The Problem</h2>
          <p className="text-body text-center max-w-3xl mx-auto mb-8">
            Most fitness apps overwhelm users with data but offer little guidance. They create anxiety through streaks and punishments, 
            and break trust with dead ends and inaccurate states. I set out to fix five core problems that undermined the user experience.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="card-professional p-6 bg-red-50 border border-red-200">
              <div className="text-2xl mb-3">🎯</div>
              <h3 className="font-semibold text-red-800 mb-2">Decision Fatigue</h3>
              <p className="text-red-700 text-sm">Users open the app and don&apos;t know what to do next. No clear hierarchy or &quot;what&apos;s next&quot; guidance.</p>
            </div>
            <div className="card-professional p-6 bg-red-50 border border-red-200">
              <div className="text-2xl mb-3">📊</div>
              <h3 className="font-semibold text-red-800 mb-2">Data Without Insight</h3>
              <p className="text-red-700 text-sm">Numbers shown without interpretation or patterns. Users see metrics but don&apos;t understand meaning.</p>
            </div>
            <div className="card-professional p-6 bg-red-50 border border-red-200">
              <div className="text-2xl mb-3">⏱️</div>
              <h3 className="font-semibold text-red-800 mb-2">Logging Friction</h3>
              <p className="text-red-700 text-sm">Too many taps to log food or start workouts. High-frequency tasks feel like chores.</p>
            </div>
            <div className="card-professional p-6 bg-red-50 border border-red-200">
              <div className="text-2xl mb-3">😰</div>
              <h3 className="font-semibold text-red-800 mb-2">Guilt-Driven UX</h3>
              <p className="text-red-700 text-sm">Streaks and punishment language cause anxiety. &quot;Streak broken&quot; creates negative emotional loops.</p>
            </div>
            <div className="card-professional p-6 bg-red-50 border border-red-200">
              <div className="text-2xl mb-3">⚠️</div>
              <h3 className="font-semibold text-red-800 mb-2">Broken Trust States</h3>
              <p className="text-red-700 text-sm">&quot;0 kcal&quot; or dead ends when data/permissions missing. The app lies instead of gracefully degrading.</p>
            </div>
            <div className="card-professional p-6 bg-emerald-50 border border-emerald-200">
              <div className="text-2xl mb-3">💡</div>
              <h3 className="font-semibold text-emerald-800 mb-2">The Opportunity</h3>
              <p className="text-emerald-700 text-sm">Design a fitness app that respects users&apos; intelligence and time—clarity over noise, insight over data.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Users & Context */}
      <section className="section-padding bg-gray-50">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-4">Users &amp; Context</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card-professional p-8 bg-white border border-slate-200">
              <h3 className="heading-subsection mb-4">Primary Users</h3>
              <p className="text-body mb-4">
                Serious trainees who work out 3–6 times per week. They&apos;re committed to their fitness goals and want a tool that matches their dedication.
              </p>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Track both nutrition and training in one place</li>
                <li>• Want accuracy over approximation</li>
                <li>• Value clarity and insight over gamification</li>
                <li>• Need speed for high-frequency logging tasks</li>
                <li>• Expect professional-grade tools, not novelty features</li>
              </ul>
            </div>

            <div className="card-professional p-8 bg-white border border-slate-200">
              <h3 className="heading-subsection mb-4">Key Behaviors</h3>
              <ul className="text-gray-700 text-sm space-y-3">
                <li className="flex items-start space-x-2">
                  <span className="text-emerald-500 mt-0.5">●</span>
                  <span><strong>Morning check-in:</strong> Quick glance at daily status and plan</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-emerald-500 mt-0.5">●</span>
                  <span><strong>Meal logging:</strong> 3–5 times daily, needs to be fast</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-emerald-500 mt-0.5">●</span>
                  <span><strong>Workout logging:</strong> Between sets, single-handed use</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-emerald-500 mt-0.5">●</span>
                  <span><strong>Weekly review:</strong> Assess progress, adjust approach</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-emerald-500 mt-0.5">●</span>
                  <span><strong>Barcode scanning:</strong> Common for packaged foods</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Goals & Success Criteria */}
      <section className="section-padding bg-white">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-4">Goals &amp; Success Criteria</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-emerald-50">
                  <th className="border border-emerald-200 px-4 py-3 text-left font-semibold text-emerald-800">Area</th>
                  <th className="border border-emerald-200 px-4 py-3 text-left font-semibold text-emerald-800">Success Criteria</th>
                </tr>
              </thead>
              <tbody>
                {successCriteria.map((item, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-200 px-4 py-3 font-medium text-gray-900">{item.area}</td>
                    <td className="border border-gray-200 px-4 py-3 text-gray-700" dangerouslySetInnerHTML={{ __html: item.criteria }} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Constraints & Considerations */}
      <section className="section-padding bg-gray-50">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-4">Constraints &amp; Considerations</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="card-professional p-6 bg-white border border-slate-200">
              <h3 className="heading-subsection mb-3">Deterministic Insights</h3>
              <p className="text-body text-sm">
                All insights use rule-based logic with predictable outputs. No AI-generated text that could produce inconsistent or confusing messages. Users can trust that the same conditions produce the same insights.
              </p>
            </div>
            <div className="card-professional p-6 bg-white border border-slate-200">
              <h3 className="heading-subsection mb-3">Permissions &amp; Offline</h3>
              <p className="text-body text-sm">
                Health data requires explicit permissions. The app must handle denied permissions, offline states, and missing data without lying (no &quot;0 kcal&quot;) or dead ends.
              </p>
            </div>
            <div className="card-professional p-6 bg-white border border-slate-200">
              <h3 className="heading-subsection mb-3">App Store Compliance</h3>
              <p className="text-body text-sm">
                Paywall must include Restore Purchases and legal disclosures. Subscription flows must meet Apple and Google requirements for transparency.
              </p>
            </div>
            <div className="card-professional p-6 bg-white border border-slate-200">
              <h3 className="heading-subsection mb-3">Calm Tone</h3>
              <p className="text-body text-sm">
                No punitive language, no guilt-inducing streaks, no urgent pressure. Copy is observational and supportive, never judgmental.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process & Approach */}
      <section className="section-padding bg-white">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-4">Process &amp; Approach</h2>
          <p className="text-body text-center max-w-3xl mx-auto mb-8">
            I worked in iterative sprints, shipping foundational features first and then continuously fixing friction points as they emerged. 
            This allowed me to validate decisions quickly and respond to real usage patterns rather than assumptions.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="card-professional p-6 bg-emerald-50 border border-emerald-200">
              <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center mb-4">
                <span className="text-white font-bold">1</span>
              </div>
              <h3 className="heading-subsection mb-3">Foundation Sprint</h3>
              <p className="text-body text-sm">
                Established core information architecture: Today page hierarchy, Add Food hub, workout logging flow, and onboarding sequence. Built the skeleton before polish.
              </p>
            </div>
            <div className="card-professional p-6 bg-emerald-50 border border-emerald-200">
              <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center mb-4">
                <span className="text-white font-bold">2</span>
              </div>
              <h3 className="heading-subsection mb-3">Friction Fixes</h3>
              <p className="text-body text-sm">
                Systematically identified and resolved usability issues: meal context bugs, broken states, navigation dead ends. Each fix improved trust and speed.
              </p>
            </div>
            <div className="card-professional p-6 bg-emerald-50 border border-emerald-200">
              <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center mb-4">
                <span className="text-white font-bold">3</span>
              </div>
              <h3 className="heading-subsection mb-3">Insight Layer</h3>
              <p className="text-body text-sm">
                Added meaning to data: training momentum, consistency tracking, goal forecasting, target zone heatmaps. Pro features that demonstrate value without pressure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution - Today Dashboard */}
      <section className="section-padding bg-gray-50">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-4">Solution: Today / Command Dashboard</h2>
          
          <div className="card-professional p-8 bg-white border border-slate-200 space-y-6">
            <h3 className="heading-subsection">Information Architecture Redesign</h3>
            <p className="text-body">
              I replaced scattered home stats with a clear hierarchy organized by user intent, not raw data. The question isn&apos;t &quot;how many calories did I eat?&quot;—it&apos;s &quot;how am I doing today?&quot; and &quot;what should I do next?&quot;
            </p>
            
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Hierarchy (Top to Bottom)</h4>
              <ol className="space-y-3 text-sm">
                <li className="flex items-start space-x-3">
                  <span className="bg-emerald-500 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                  <div>
                    <strong>Context Header (Daily Focus)</strong>
                    <p className="text-gray-600">A single sentence that sets context for the day based on goals and progress.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="bg-emerald-500 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                  <div>
                    <strong>Nutrition Today + Training Today</strong>
                    <p className="text-gray-600">Mirrored anchor cards that answer the two key questions: &quot;How am I eating?&quot; and &quot;Should I train?&quot;</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="bg-emerald-500 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                  <div>
                    <strong>Protocol Insight</strong>
                    <p className="text-gray-600">Single Pro surface with one upgrade CTA—consolidated from multiple locked cards.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="bg-emerald-500 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">4</span>
                  <div>
                    <strong>Quick Actions</strong>
                    <p className="text-gray-600">Log food / Start workout / Scan barcode—the three most common intents, always accessible.</p>
                  </div>
                </li>
              </ol>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <h4 className="font-semibold text-emerald-800 mb-2">Design Rationale</h4>
              <p className="text-emerald-700 text-sm">
                By organizing around intent rather than data type, users can assess daily status in one scroll. No duplicate metrics, no cognitive load deciding where to look. Each card has a single primary metric.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution - Onboarding */}
      <section className="section-padding bg-white">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-4">Solution: Goal-Driven Onboarding</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card-professional p-6 bg-slate-50 border border-slate-200">
              <h3 className="heading-subsection mb-4">Progressive Disclosure</h3>
              <p className="text-body text-sm mb-4">
                I condensed onboarding from 8 steps to 6, auto-calculating targets from meaningful inputs rather than asking users to do math.
              </p>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Goals, training context, nutrition approach</li>
                <li>• Goal weight + pace (detect cutting/bulking/maintenance)</li>
                <li>• &quot;Skip for now&quot; is always frictionless</li>
                <li>• Optional &quot;Accuracy Mode&quot; for advanced users</li>
              </ul>
            </div>

            <div className="card-professional p-6 bg-slate-50 border border-slate-200">
              <h3 className="heading-subsection mb-4">Transparent Targets</h3>
              <p className="text-body text-sm mb-4">
                Users see exactly how their targets are calculated—TDEE, adjustments, final targets, macros. No black box.
              </p>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Targets Summary shows the math</li>
                <li>• Accuracy Mode reveals extra body metrics (height/age/sex)</li>
                <li>• Users can adjust if they know better</li>
                <li>• Builds trust through transparency</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Solution - Nutrition Logging */}
      <section className="section-padding bg-gray-50">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-4">Solution: Nutrition Logging System</h2>
          
          {/* Nutrition Screenshot */}
          <div className="flex justify-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_1def07b2-6293-4c22-9598-3b0db649a536/artifacts/ffth27ya_Nutrition.jpg"
              alt="My Protocol Food Diary - Nutrition Without Chaos: Macro tracking that stays calm and accurate"
              className="w-full max-w-2xl rounded-2xl shadow-xl"
            />
          </div>
          
          <div className="card-professional p-8 bg-white border border-slate-200 space-y-6">
            <h3 className="heading-subsection">Hub-and-Spoke Architecture</h3>
            <p className="text-body">
              The Add Food screen is a hub with multiple spoke paths based on user intent. Each path is optimized for speed and preserves meal context end-to-end.
            </p>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                <div className="text-2xl mb-2">📷</div>
                <h4 className="font-semibold text-blue-800">Scan Barcode</h4>
                <p className="text-blue-700 text-xs mt-1">Found / Not found / Manual fallback</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                <div className="text-2xl mb-2">🔍</div>
                <h4 className="font-semibold text-blue-800">Search</h4>
                <p className="text-blue-700 text-xs mt-1">USDA first for generic; branded for specific</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                <div className="text-2xl mb-2">🕐</div>
                <h4 className="font-semibold text-blue-800">Recent</h4>
                <p className="text-blue-700 text-xs mt-1">Quick re-log of common foods</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                <div className="text-2xl mb-2">💾</div>
                <h4 className="font-semibold text-blue-800">Saved Meals</h4>
                <p className="text-blue-700 text-xs mt-1">Multi-food combinations</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                <div className="text-2xl mb-2">📋</div>
                <h4 className="font-semibold text-blue-800">My Foods</h4>
                <p className="text-blue-700 text-xs mt-1">Personal library + custom entries</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                <div className="text-2xl mb-2">⚡</div>
                <h4 className="font-semibold text-blue-800">Quick Add</h4>
                <p className="text-blue-700 text-xs mt-1">Direct calorie entry</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <h4 className="font-semibold text-emerald-800 mb-2">Meal Context Preserved</h4>
                <p className="text-emerald-700 text-sm">
                  When a user taps &quot;Breakfast,&quot; that context flows through search, food detail, and logging. Fixed a bug where it always showed &quot;Snacks.&quot;
                </p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <h4 className="font-semibold text-emerald-800 mb-2">Serving Flexibility</h4>
                <p className="text-emerald-700 text-sm">
                  Supports serving units vs grams based on available data. Unknown barcodes can be saved to personal library for future use.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution - Training Logging */}
      <section className="section-padding bg-white">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-4">Solution: Training Logging &amp; Routines</h2>
          
          {/* Workout Screenshot */}
          <div className="flex justify-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_1def07b2-6293-4c22-9598-3b0db649a536/artifacts/9fvze36y_Workout.jpg"
              alt="My Protocol Routine Details - Training First: Track training with precision, not clutter"
              className="w-full max-w-2xl rounded-2xl shadow-xl"
            />
          </div>
          
          <div className="card-professional p-8 bg-slate-50 border border-slate-200 space-y-6">
            <h3 className="heading-subsection">Between-Sets UX</h3>
            <p className="text-body">
              Workout logging happens between sets—often single-handed, with limited attention. Every tap counts. I designed for speed without sacrificing accuracy.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Fast Set Logging</h4>
                <ul className="text-gray-700 text-sm space-y-2">
                  <li>• Auto-fill from history (last weight/reps)</li>
                  <li>• Inline editing (no modals)</li>
                  <li>• Tap-to-complete + haptic feedback</li>
                  <li>• Warmup toggle for non-working sets</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Mental Model Alignment</h4>
                <ul className="text-gray-700 text-sm space-y-2">
                  <li>• Renamed &quot;Templates&quot; → &quot;Routines&quot; (user language)</li>
                  <li>• &quot;Save as Routine&quot; prompt after ad-hoc workout</li>
                  <li>• Direct Routine Start (removed intermediate page)</li>
                  <li>• Routine detail → Start directly into workout</li>
                </ul>
              </div>
            </div>

            <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
              <h4 className="font-semibold text-teal-800 mb-2">Key Decision</h4>
              <p className="text-teal-700 text-sm">
                Removed the &quot;Start Workout&quot; intermediate page. When a user taps a routine, they want to start training—not read a summary. Reduced ~2 navigation steps.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution - Insights Layer */}
      <section className="section-padding bg-gray-50">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-4">Solution: Insights Layer</h2>
          <p className="text-body text-center max-w-3xl mx-auto mb-8">
            Data without interpretation is just noise. I designed an insights layer that gives meaning to numbers—turning raw metrics into actionable understanding.
          </p>
          
          {/* Insights & Trends Screenshots */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="flex flex-col items-center">
              <img
                src="https://customer-assets.emergentagent.com/job_1def07b2-6293-4c22-9598-3b0db649a536/artifacts/cq3fp6xr_Insights.jpg"
                alt="My Protocol Insight - Pro Insight: Understand what's working – and what to adjust"
                className="w-full max-w-md rounded-2xl shadow-xl"
              />
            </div>
            <div className="flex flex-col items-center">
              <img
                src="https://customer-assets.emergentagent.com/job_1def07b2-6293-4c22-9598-3b0db649a536/artifacts/ti71pdmq_Trends.jpg"
                alt="My Protocol Trends - Trends & History: See patterns over time, not raw noise"
                className="w-full max-w-md rounded-2xl shadow-xl"
              />
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card-professional p-6 bg-white border border-slate-200">
              <h3 className="heading-subsection mb-4">Training Insights</h3>
              <ul className="text-gray-700 text-sm space-y-3">
                <li className="flex items-start space-x-2">
                  <span className="text-emerald-500 font-bold">●</span>
                  <span><strong>Momentum:</strong> Building / Stable / Slipping</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-emerald-500 font-bold">●</span>
                  <span><strong>Consistency:</strong> 4-week percentage</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-emerald-500 font-bold">●</span>
                  <span><strong>Goal Forecasting:</strong> Projected timeline</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-emerald-500 font-bold">●</span>
                  <span><strong>Training Load:</strong> Volume over time chart</span>
                </li>
              </ul>
            </div>

            <div className="card-professional p-6 bg-white border border-slate-200">
              <h3 className="heading-subsection mb-4">Nutrition Insights</h3>
              <ul className="text-gray-700 text-sm space-y-3">
                <li className="flex items-start space-x-2">
                  <span className="text-blue-500 font-bold">●</span>
                  <span><strong>Target Zone Heatmap:</strong> Days in/out of range</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-500 font-bold">●</span>
                  <span><strong>Calorie Volatility Band:</strong> Consistency visualization</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-500 font-bold">●</span>
                  <span><strong>Protein Distribution:</strong> Timing curve</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-500 font-bold">●</span>
                  <span><strong>Quiet Streak Recognition:</strong> No guilt, just observation</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="card-professional p-6 bg-amber-50 border border-amber-200">
            <h3 className="heading-subsection mb-3">Copy Principles</h3>
            <p className="text-amber-800 text-sm">
              All copy uses calm, observational language. No emojis. No &quot;streak broken&quot; guilt. Instead of &quot;You missed your goal!&quot; we say &quot;Yesterday was above target.&quot; 
              The app notices patterns without judging them.
            </p>
          </div>
        </div>
      </section>

      {/* Solution - Pro & Monetization */}
      <section className="section-padding bg-white">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-4">Solution: Monetization with Respect</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card-professional p-6 bg-slate-50 border border-slate-200">
              <h3 className="heading-subsection mb-4">Consolidated Pro Surface</h3>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• ONE Protocol Insight card with ONE upgrade CTA</li>
                <li>• Replaced multiple scattered locked cards</li>
                <li>• Blurred preview (tease value, don&apos;t block)</li>
                <li>• Paywall includes Restore Purchases + legal disclosures</li>
              </ul>
            </div>

            <div className="card-professional p-6 bg-slate-50 border border-slate-200">
              <h3 className="heading-subsection mb-4">Frequency Limits</h3>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Max 1 upsell prompt per day</li>
                <li>• Max 4 per week</li>
                <li>• Suppression after trial skip (48–72h)</li>
                <li>• Never interrupt core logging flows</li>
              </ul>
            </div>
          </div>

          <div className="card-professional p-6 bg-red-50 border border-red-200">
            <h3 className="heading-subsection mb-3">Health Data Resilience</h3>
            <p className="text-red-800 text-sm mb-4">
              Never show &quot;0 kcal burned&quot;—it&apos;s either wrong or a lie. I implemented a fallback hierarchy:
            </p>
            <ol className="text-red-700 text-sm space-y-1">
              <li>1. Health app active energy (if connected + permitted)</li>
              <li>2. Estimate from logged workouts</li>
              <li>3. &quot;Not connected&quot; state with Connect CTA</li>
              <li>4. Offline cached messaging</li>
            </ol>
          </div>
        </div>
      </section>

      {/* Key Screens / IA */}
      <section className="section-padding bg-gray-50">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-4">Key Screens &amp; Information Architecture</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { screen: "Today Page", answers: "What&apos;s my status? What should I do next?" },
              { screen: "Nutrition Today Card", answers: "How am I tracking against my calorie/macro targets?" },
              { screen: "Training Today Card", answers: "Should I train? What&apos;s my recent momentum?" },
              { screen: "Add Food Hub", answers: "How do I log this food fastest?" },
              { screen: "Barcode Flow", answers: "Is this product in the database? What if not?" },
              { screen: "Food Detail", answers: "How much am I logging? For which meal?" },
              { screen: "Workout Logger", answers: "What weight/reps for this set?" },
              { screen: "Routine Detail", answers: "What exercises? Ready to start?" },
              { screen: "Post-Workout", answers: "Should I save this as a routine?" },
              { screen: "Training Insights", answers: "Am I building momentum or slipping?" },
              { screen: "Target Zone Heatmap", answers: "How consistent am I over time?" },
              { screen: "Permission States", answers: "Why is data missing? How do I fix it?" }
            ].map((item, index) => (
              <div key={index} className="card-professional p-4 bg-white border border-slate-200">
                <h4 className="font-semibold text-gray-900 text-sm mb-1">{item.screen}</h4>
                <p className="text-gray-600 text-xs" dangerouslySetInnerHTML={{ __html: item.answers }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Usability Issues & Fixes */}
      <section className="section-padding bg-white">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-4">Usability Issues &amp; Fixes</h2>
          <p className="text-body text-center max-w-3xl mx-auto mb-8">
            Iterative development means continuously finding and fixing friction. Here are the key issues I identified and resolved.
          </p>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-slate-100">
                  <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-800">Issue</th>
                  <th className="border border-slate-200 px-3 py-2 text-center font-semibold text-slate-800 w-20">Impact</th>
                  <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-800">Fix</th>
                </tr>
              </thead>
              <tbody>
                {usabilityFixes.map((item, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-200 px-3 py-2 text-gray-700" dangerouslySetInnerHTML={{ __html: item.issue }} />
                    <td className={`border border-gray-200 px-3 py-2 text-center font-medium ${
                      item.impact === "High" ? "text-red-600" : item.impact === "Medium" ? "text-amber-600" : "text-gray-600"
                    }`}>{item.impact}</td>
                    <td className="border border-gray-200 px-3 py-2 text-gray-700">{item.fix}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="section-padding bg-emerald-50">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-4">Outcomes</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card-professional p-8 bg-white border border-emerald-200">
              <h3 className="heading-subsection mb-6 text-emerald-800">Quantitative</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-emerald-100 pb-3">
                  <span className="text-gray-700">Onboarding steps</span>
                  <span className="font-bold text-emerald-600">8 → 6</span>
                </div>
                <div className="flex items-center justify-between border-b border-emerald-100 pb-3">
                  <span className="text-gray-700">Workout start navigation</span>
                  <span className="font-bold text-emerald-600">-2 steps</span>
                </div>
                <div className="flex items-center justify-between border-b border-emerald-100 pb-3">
                  <span className="text-gray-700">Barcode flow</span>
                  <span className="font-bold text-emerald-600">-1 step</span>
                </div>
                <div className="flex items-center justify-between border-b border-emerald-100 pb-3">
                  <span className="text-gray-700">Meal context accuracy</span>
                  <span className="font-bold text-emerald-600">Reliable</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">Exercise search results</span>
                  <span className="font-bold text-emerald-600">100 → Full library</span>
                </div>
              </div>
            </div>

            <div className="card-professional p-8 bg-white border border-emerald-200">
              <h3 className="heading-subsection mb-6 text-emerald-800">Qualitative</h3>
              <ul className="text-gray-700 space-y-3">
                <li className="flex items-start space-x-2">
                  <span className="text-emerald-500 mt-1">✓</span>
                  <span>Users can assess daily status in one scroll (two anchor cards)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-emerald-500 mt-1">✓</span>
                  <span>Faster, calmer logging with fewer dead ends</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-emerald-500 mt-1">✓</span>
                  <span>Improved trust in progress indicators and data accuracy</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-emerald-500 mt-1">✓</span>
                  <span>Pro conversion via demonstrated value, not pressure</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-emerald-500 mt-1">✓</span>
                  <span>No more broken states or lying metrics</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Design Principles */}
      <section className="section-padding bg-white">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-4">Design Principles</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {designPrinciples.map((item, index) => (
              <div key={index} className="card-professional p-6 bg-slate-50 border border-slate-200">
                <h3 className="font-bold text-gray-900 mb-2">{item.principle}</h3>
                <p className="text-gray-600 text-sm" dangerouslySetInnerHTML={{ __html: item.description }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What I'd Improve Next */}
      <section className="section-padding bg-gray-50">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-4">What I&apos;d Improve Next</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="card-professional p-6 bg-white border border-slate-200">
              <h3 className="heading-subsection mb-3">Instrumentation</h3>
              <p className="text-body text-sm">
                Add analytics to validate assumptions: time-to-log metrics, drop-off points in flows, feature adoption rates. Data to inform the next iteration.
              </p>
            </div>
            <div className="card-professional p-6 bg-white border border-slate-200">
              <h3 className="heading-subsection mb-3">Usability Testing</h3>
              <p className="text-body text-sm">
                Conduct moderated sessions with target users. Watch them log meals and workouts. Identify friction I can&apos;t see from the inside.
              </p>
            </div>
            <div className="card-professional p-6 bg-white border border-slate-200">
              <h3 className="heading-subsection mb-3">A/B Testing: Scan Flow</h3>
              <p className="text-body text-sm">
                Test variations of the barcode not-found flow. Does showing similar products reduce friction? Or does manual entry work better?
              </p>
            </div>
            <div className="card-professional p-6 bg-white border border-slate-200">
              <h3 className="heading-subsection mb-3">Accessibility Audit</h3>
              <p className="text-body text-sm">
                Review VoiceOver/TalkBack support, contrast ratios, touch target sizes. Ensure the app works for users with different abilities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Reflection */}
      <section className="section-padding bg-white">
        <div className="container-narrow space-y-8">
          <h2 className="heading-section text-center mb-4">Reflection</h2>
          
          <div className="prose prose-lg max-w-3xl mx-auto text-gray-700">
            <p>
              The most important lesson from this project was the power of subtraction. Every fitness app tries to add features. I found more value in removing: removing unnecessary steps, removing guilt language, removing broken states that erode trust. 
            </p>
            <p>
              Trust is fragile. When an app shows &quot;0 kcal burned&quot; and the user knows they just worked out, they stop believing anything the app says. I spent significant effort on graceful degradation—handling every edge case where data might be missing, permissions denied, or network unavailable. The goal was never to lie to the user.
            </p>
            <p>
              I also learned to respect user intent. When someone opens the Today page, they&apos;re not asking &quot;show me all my data.&quot; They&apos;re asking &quot;how am I doing?&quot; and &quot;what should I do next?&quot; Organizing around those questions—rather than data categories—made the experience dramatically clearer.
            </p>
            <p>
              Finally, monetization doesn&apos;t have to be adversarial. By demonstrating Pro value through blurred previews rather than hard locks, and by limiting upsell frequency, we can convert users through genuine value rather than pressure. Respect begets loyalty.
            </p>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="container-narrow">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <button
              type="button"
              onClick={() => window.history.back()}
              className="interactive-link flex items-center"
              data-testid="back-to-portfolio"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Portfolio
            </button>

            <div className="text-center">
              <div className="text-sm text-gray-500 mb-2">Case Study: My Protocol</div>
            </div>
          </div>
        </div>
      </section>
    </CaseStudyLayout>
  );
};

export default MyProtocol;
